import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const PaymentProcessor = {
    Lawpay: "lawpay",
    Payrix: "payrix"
};
export type PaymentProcessorType = typeof PaymentProcessor[keyof typeof PaymentProcessor];
export const CaseAccess = {
    Everyone: "everyone",
    Listed: "listed"
};
export type CaseAccessType = typeof CaseAccess[keyof typeof CaseAccess];
export type FirmEmailCopies = {enabled: boolean, shared: boolean, default?: {addresses: string[], copyAs: 'cc' | 'bcc' | 'none', replyTo?: string}, categories?: {[category: string]: {addresses: string[], copyAs: 'cc' | 'bcc' | 'none', replyTo?: string}}};
class Firm extends Base implements EntityType {
    legalName: string;
    billingName?: string | null;
    displayName?: string | null;
    stripeCustomerId?: string | null;
    stripeSubscriptionId?: string | null;
    paymentProcessor?: PaymentProcessorType | null;
    logo?: uuid | null;
    sendToEmail?: string | null;
    ccToEmail?: string | null;
    bccToEmail?: string | null;
    replyToEmail?: string | null;
    emailCopies?: FirmEmailCopies | null;
    payrixLoginId?: string | null;
    payrixEntityId?: string | null;
    sameDayPayout: boolean;
    country: string;
    language_locale: string;
    timeZone?: string | null;
    caseAccess: CaseAccessType;
    caseAccessRoleIds: uuid[];
    constructor(doc: typeof Firm | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            legalName: null,
            caseAccess: "everyone",
            caseAccessRoleIds: [],
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "firm";
        // @ts-ignore
        this.legalName = doc._legalName || doc.legalName || doc.legalname || null;
        // @ts-ignore
        this.billingName = doc._billingName || doc.billingName || doc.billingname || null;
        // @ts-ignore
        this.displayName = doc._displayName || doc.displayName || doc.displayname || null;
        // @ts-ignore
        this.stripeCustomerId = doc._stripeCustomerId || doc.stripeCustomerId || doc.stripecustomerid || null;
        // @ts-ignore
        this.stripeSubscriptionId = doc._stripeSubscriptionId || doc.stripeSubscriptionId || doc.stripesubscriptionid || null;
        // @ts-ignore
        this.paymentProcessor = doc._paymentProcessor || doc.paymentProcessor || doc.paymentprocessor || null;
        // @ts-ignore
        this.logo = doc._logo || doc.logo || null;
        // @ts-ignore
        this.sendToEmail = doc._sendToEmail || doc.sendToEmail || doc.sendtoemail || null;
        // @ts-ignore
        this.ccToEmail = doc._ccToEmail || doc.ccToEmail || doc.cctoemail || null;
        // @ts-ignore
        this.bccToEmail = doc._bccToEmail || doc.bccToEmail || doc.bcctoemail || null;
        // @ts-ignore
        this.replyToEmail = doc._replyToEmail || doc.replyToEmail || doc.replytoemail || null;
        // @ts-ignore
        this.emailCopies = doc._emailCopies || doc.emailCopies || doc.emailcopies || {};
        // @ts-ignore
        this.payrixLoginId = doc._payrixLoginId || doc.payrixLoginId || doc.payrixloginid || null;
        // @ts-ignore
        this.payrixEntityId = doc._payrixEntityId || doc.payrixEntityId || doc.payrixentityid || null;
        // @ts-ignore
        this.sameDayPayout = util.defaultBoolean([doc.sameDayPayout, doc._sameDayPayout, doc.samedaypayout, false]);
        // @ts-ignore
        this.country = doc._country || doc.country || "US";
        // @ts-ignore
        this.language_locale = doc._language_locale || doc.language_locale || "en-US";
        // @ts-ignore
        this.timeZone = doc._timeZone || doc.timeZone || doc.timezone || null;
        // @ts-ignore
        this.caseAccess = doc._caseAccess || doc.caseAccess || doc.caseaccess || "everyone";
        // @ts-ignore
        this.caseAccessRoleIds = doc._caseAccessRoleIds || doc.caseAccessRoleIds || doc.caseaccessroleids || [];
    }
    get id(): uuid { return this.firmId; }
    static PAYMENT_PROCESSOR_VALUES = ["lawpay", "payrix"];
    static CASE_ACCESS_VALUES = ["everyone", "listed"];
    addCaseAccessRoleId(caseAccessRoleId: uuid): uuid {
        if (!caseAccessRoleId) throw new ValidationError('No nulls allowed in an array!');
        if (!this.caseAccessRoleIds) this.caseAccessRoleIds = [];
        this.caseAccessRoleIds.push(caseAccessRoleId);
        return caseAccessRoleId;
    }
    updateCaseAccessRoleId(caseAccessRoleId: uuid) {
        if (!this.caseAccessRoleIds) this.caseAccessRoleIds = [];
        const index = this.caseAccessRoleIds.findIndex(item => item === caseAccessRoleId);
        if (index !== -1) { this.caseAccessRoleIds[index] = caseAccessRoleId; }
        else this.addCaseAccessRoleId(caseAccessRoleId);
    }
    removeCaseAccessRoleId(caseAccessRoleId: uuid) {
        if (!this.caseAccessRoleIds) return;
        let foundItems = this.caseAccessRoleIds?.filter(item => item === caseAccessRoleId);
        if (foundItems && foundItems.length > 0) {
            this.caseAccessRoleIds = this.caseAccessRoleIds.filter(item => !(item === caseAccessRoleId));
        }
    }
    abandonCaseAccessRoleIds() { this.caseAccessRoleIds = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","comment":"Associated firm"}]; }
    toLowerCase() { return this.firmId; }
    static override getNonRefArrayFields(): string[] { return ["caseAccessRoleIds"]; }
    static override getDefaultEntries(): Firm[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'firm',
    dataArea: 'Firm Settings',
    table: 'FIRM',
    comment: `A law firm that subscribes to Outlaw Practice. This is the top-level multi-tenant root: every case, contact, invoice, and user belongs to a firm. Stores the firm's identity (legal, billing, and display names), its Stripe subscription for platform billing, an optional payment-processor connection (LawPay or Payrix), system email routing addresses, logo, and locale.`,
    fields: {
        legalName: {
            // OWA-415: `unique` was a no-op label (never DB-enforced). Now that `unique`
            // generates a real owner-scoped index it would be a trivial per-firm no-op
            // here (one row per firm) and prod has two firms sharing a legal name, so the
            // honest move is to drop it rather than claim a uniqueness we don't enforce.
            type: 'string', maxLength: 128, required: true, comment: 'Legal entity name',
        },

        billingName: {
            type: 'string', maxLength: 128, comment: 'Entity name used for billing statements'
        },

        displayName: {
            type: 'string', maxLength: 128, comment: 'Entity name displayed in the UI'
        },

        /**
         * This Stripe information has the customer info for billing in Outlaw, NOT for a payment processor and
         * payment.
         *
         * That information is stored in the paymentProcessor field.
         */
        stripeCustomerId: {
            type: 'string', serverOwned: true, comment: 'customerId field from Stripe.com'
        },

        stripeSubscriptionId: {
            type: 'string', serverOwned: true, comment: 'subscriptionId field from Stripe.com'
        },

        /**
         * The payment processor in use (connected), or blank for no payment processing
         */
        paymentProcessor: {
            type: 'string', values: ['lawpay', 'payrix'], serverOwned: true, comment: 'Payment processor in use (connected)'
        },

        /**
         * Firm logo, for use in things like invoices and receipts.
         */
        logo: {
            type: 'uuid', entityType: 'document', comment: 'Document link to an icon for the firm'
        },

        /**
         * When the system generates emails to the firm, what email address should it use?
         */
        sendToEmail: {
            type: 'string', comment: 'Send-To address for emails sent by the system'
        },

        /**
         * In addition to the sendToEmail, should the system also send a copy to this address?
         */
        ccToEmail: {
            type: 'string', comment: 'CC-To address for emails sent by the system'
        },

        bccToEmail: {
            type: 'string', comment: 'BCC-To address for emails sent by the system'
        },

        /**
         * Reply-To address for emails sent by the system.
         */
        replyToEmail: {
            type: 'string', comment: 'Reply-To address for emails sent by the system'
        },

        /**
         * Who at the firm is copied on emails to clients, how, and the Reply-To, per category
         * (invoices, payments). The payments addresses also receive the firm's payment notices.
         * Until it holds a boolean `enabled` (the client saves `{}`), the four fields above apply
         * as before (OWA-1288).
         */
        emailCopies: {
            type: 'json',
            // A save that carries no setting (an older client, or a page that has not loaded it)
            // must not clear it.
            keptWhenOmitted: true,
            jsdoc: `{enabled: boolean, shared: boolean, default?: {addresses: string[], copyAs: 'cc' | 'bcc' | 'none', replyTo?: string}, categories?: {[category: string]: {addresses: string[], copyAs: 'cc' | 'bcc' | 'none', replyTo?: string}}}`,
            comment: 'Firm copies of client emails and firm notice addresses, per category (invoices, payments)'
        },

        /**
         * The Payrix login ID for the firm (created when the application is made).
         */
        payrixLoginId: {
            type: 'string', serverOwned: true, comment: 'Payrix login ID for this firm'
        },

        payrixEntityId: {
            type: 'string', serverOwned: true, comment: 'Payrix entity ID for this firm'
        },

        /**
         * When true, Payrix payouts for this firm are created with same-day funding.
         * Same-day funding costs 0.1% of the payout total, so it is opt-in per firm.
         */
        sameDayPayout: {
            type: 'boolean', default: false, comment: 'Enable Payrix same-day payouts (costs 0.1% of total)'
        },

        country: {
            type: 'string', default: 'US', comment: 'Country where the firm is based'
        },

        language_locale: {
            type: 'string', default: 'en-US', comment: `The firm's locale, e.g. en/US or en/CA`
        },

        /**
         * OWA-1463: IANA time zone name of the firm, e.g. "America/Chicago". Invoice periods and
         * trust balances as of a date count a transaction on the firm's calendar day, so everyone
         * at the firm sees the same figures wherever their computer is. Null means not set: the
         * client then uses each viewer's own zone, as it did before this field existed.
         */
        timeZone: {
            type: 'string',
            maxLength: 64,
            // A save that carries no zone (an older client, or a page that has not loaded it)
            // must not clear it.
            keptWhenOmitted: true,
            comment: 'IANA time zone name of the firm (e.g. America/Chicago); null when not set'
        },
        caseAccess: {
            type: 'string',
            serverOwned: true,
            values: ['everyone', 'listed'],
            required: true,
            default: 'everyone',
            comment: 'Who sees Cases they are not on the team for: everyone, or listed, meaning only owners, admins and the listed Role ids. A Case with no team is visible to every Firm User. Set only by owners and admins through server routes.'
        },
        caseAccessRoleIds: {
            type: 'uuid[]',
            serverOwned: true,
            required: true,
            default: [],
            comment: 'When caseAccess is listed, only owners, admins and these Role ids see Cases they are not on the team for. A Case with no team is visible to every Firm User. Set only by owners and admins through server routes.'
        }
    },
    sortOrder: [
        {position: 1, fieldName: 'legalName', asc: true}
    ],
    maps: [
        {name: 'logo', keysFn: t => t.logo, presentFn: (t, k) => t.logo === k}
    ]
});
    }
}
export default Firm;
